import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../components/ui/accordion';
import {
  CodeBlock,
  CodeBlockBody,
  CodeBlockContent,
  CodeBlockCopyButton,
  CodeBlockFilename,
  CodeBlockFiles,
  CodeBlockHeader,
  CodeBlockItem,
  CodeBlockSelect,
  CodeBlockSelectContent,
  CodeBlockSelectItem,
  CodeBlockSelectTrigger,
  CodeBlockSelectValue
} from '../../../../components/kibo-ui/code-block';
import {
  getInstagramUser,
  getInstagramAccountInsights,
  getInstagramMediaInsights
} from '../../../../services/socials/instagramInsightsService';

const LOGIN_TYPE_OPTIONS = [
  { value: 'facebook', label: 'Facebook Login (graph.facebook.com)' },
  { value: 'instagram', label: 'Instagram Login (graph.instagram.com)' }
];

const PERIOD_OPTIONS = [
  { value: 'day', label: 'day' },
  { value: 'week', label: 'week' },
  { value: 'month', label: 'month' },
  { value: 'lifetime', label: 'lifetime' }
];

export default function InstagramInsightsPage() {
  const [loginType, setLoginType] = useState('facebook');
  const [accessToken, setAccessToken] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountMetrics, setAccountMetrics] = useState('');
  const [accountPeriod, setAccountPeriod] = useState('day');
  const [mediaId, setMediaId] = useState('');
  const [mediaMetrics, setMediaMetrics] = useState('');
  const [accountResponse, setAccountResponse] = useState(null);
  const [mediaResponse, setMediaResponse] = useState(null);
  const [userResponse, setUserResponse] = useState(null);
  const [accountError, setAccountError] = useState('');
  const [mediaError, setMediaError] = useState('');
  const [userError, setUserError] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);

  const accountCodeData = useMemo(() => {
    if (!accountResponse) return [];
    return [
      {
        language: 'json',
        filename: 'account-insights.json',
        code: JSON.stringify(accountResponse, null, 2)
      }
    ];
  }, [accountResponse]);

  const mediaCodeData = useMemo(() => {
    if (!mediaResponse) return [];
    return [
      {
        language: 'json',
        filename: 'media-insights.json',
        code: JSON.stringify(mediaResponse, null, 2)
      }
    ];
  }, [mediaResponse]);

  const handleAccountInsights = async () => {
    setAccountError('');
    setAccountResponse(null);
    setAccountLoading(true);
    try {
      const response = await getInstagramAccountInsights({
        instagramUserId: accountId?.trim(),
        metrics: accountMetrics,
        period: accountPeriod,
        accessToken: accessToken?.trim(),
        loginType
      });
      setAccountResponse(response);
    } catch (error) {
      setAccountError(error?.message || 'Error obteniendo insights de cuenta');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleFetchUser = async () => {
    setUserError('');
    setUserResponse(null);
    setUserLoading(true);
    try {
      const response = await getInstagramUser({
        accessToken: accessToken?.trim(),
        loginType
      });
      setUserResponse(response);
      if (response?.id) {
        setAccountId(response.id);
      }
    } catch (error) {
      setUserError(error?.message || 'Error obteniendo usuario');
    } finally {
      setUserLoading(false);
    }
  };

  const handleMediaInsights = async () => {
    setMediaError('');
    setMediaResponse(null);
    setMediaLoading(true);
    try {
      const response = await getInstagramMediaInsights({
        instagramMediaId: mediaId?.trim(),
        metrics: mediaMetrics,
        accessToken: accessToken?.trim(),
        loginType
      });
      setMediaResponse(response);
    } catch (error) {
      setMediaError(error?.message || 'Error obteniendo insights de media');
    } finally {
      setMediaLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="fancyyool-highlight-blue">Instagram</span> Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo de login</Label>
              <Select value={loginType} onValueChange={setLoginType}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona tipo de login" />
                </SelectTrigger>
                <SelectContent>
                  {LOGIN_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Access Token</Label>
              <Input
                type="password"
                value={accessToken}
                onChange={(event) => setAccessToken(event.target.value)}
                placeholder="Se usa REACT_APP_INSTAGRAM_ACCESS_TOKEN si se deja vacío"
              />
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={handleFetchUser} disabled={userLoading}>
                {userLoading ? 'Consultando...' : 'Obtener Instagram User ID'}
              </Button>
              {userResponse?.id && (
                <div className="text-sm text-muted-foreground">
                  ID: {userResponse.id}
                </div>
              )}
              {userResponse?.username && (
                <div className="text-sm text-muted-foreground">
                  @{userResponse.username}
                </div>
              )}
            </div>
            {userError && (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{userError}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Insights de <span className="fancyyool-highlight-violet">cuenta</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Instagram User ID</Label>
              <Input
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Métricas</Label>
              <Input
                value={accountMetrics}
                onChange={(event) => setAccountMetrics(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Periodo</Label>
              <Select value={accountPeriod} onValueChange={setAccountPeriod}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona periodo" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleAccountInsights} disabled={accountLoading}>
              {accountLoading ? 'Consultando...' : 'Obtener insights de cuenta'}
            </Button>
          </div>
          {accountError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{accountError}</AlertDescription>
            </Alert>
          )}
          {accountCodeData.length > 0 && (
            <CodeBlock data={accountCodeData} defaultValue={accountCodeData[0]?.language}>
              <CodeBlockHeader>
                <CodeBlockFiles>
                  {(file) => (
                    <CodeBlockFilename key={file.language} value={file.language}>
                      {file.filename}
                    </CodeBlockFilename>
                  )}
                </CodeBlockFiles>
                <div className="flex items-center gap-2">
                  <CodeBlockSelect>
                    <CodeBlockSelectTrigger>
                      <CodeBlockSelectValue />
                    </CodeBlockSelectTrigger>
                    <CodeBlockSelectContent>
                      {(file) => (
                        <CodeBlockSelectItem key={file.language} value={file.language}>
                          {file.language}
                        </CodeBlockSelectItem>
                      )}
                    </CodeBlockSelectContent>
                  </CodeBlockSelect>
                  <CodeBlockCopyButton />
                </div>
              </CodeBlockHeader>
              <CodeBlockBody>
                {(file) => (
                  <CodeBlockItem key={file.language} value={file.language}>
                    <CodeBlockContent language={file.language}>{file.code}</CodeBlockContent>
                  </CodeBlockItem>
                )}
              </CodeBlockBody>
            </CodeBlock>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Insights de <span className="fancyyool-highlight-teal">media</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Instagram Media ID</Label>
              <Input
                value={mediaId}
                onChange={(event) => setMediaId(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Métricas</Label>
              <Input
                value={mediaMetrics}
                onChange={(event) => setMediaMetrics(event.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleMediaInsights} disabled={mediaLoading}>
              {mediaLoading ? 'Consultando...' : 'Obtener insights de media'}
            </Button>
          </div>
          {mediaError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{mediaError}</AlertDescription>
            </Alert>
          )}
          {mediaCodeData.length > 0 && (
            <CodeBlock data={mediaCodeData} defaultValue={mediaCodeData[0]?.language}>
              <CodeBlockHeader>
                <CodeBlockFiles>
                  {(file) => (
                    <CodeBlockFilename key={file.language} value={file.language}>
                      {file.filename}
                    </CodeBlockFilename>
                  )}
                </CodeBlockFiles>
                <div className="flex items-center gap-2">
                  <CodeBlockSelect>
                    <CodeBlockSelectTrigger>
                      <CodeBlockSelectValue />
                    </CodeBlockSelectTrigger>
                    <CodeBlockSelectContent>
                      {(file) => (
                        <CodeBlockSelectItem key={file.language} value={file.language}>
                          {file.language}
                        </CodeBlockSelectItem>
                      )}
                    </CodeBlockSelectContent>
                  </CodeBlockSelect>
                  <CodeBlockCopyButton />
                </div>
              </CodeBlockHeader>
              <CodeBlockBody>
                {(file) => (
                  <CodeBlockItem key={file.language} value={file.language}>
                    <CodeBlockContent language={file.language}>{file.code}</CodeBlockContent>
                  </CodeBlockItem>
                )}
              </CodeBlockBody>
            </CodeBlock>
          )}
        </CardContent>
      </Card>

      <Accordion type="single" collapsible>
        <AccordionItem value="requisitos">
          <AccordionTrigger>Requisitos y notas</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium">Permisos Instagram Login</div>
                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                  <li>instagram_business_basic</li>
                  <li>instagram_business_manage_insights</li>
                </ul>
              </div>
              <div>
                <div className="text-sm font-medium">Permisos Facebook Login</div>
                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                  <li>instagram_basic</li>
                  <li>instagram_manage_insights</li>
                  <li>pages_read_engagement</li>
                  <li>ads_management</li>
                  <li>ads_read</li>
                </ul>
              </div>
              <div className="text-sm text-muted-foreground">
                Timestamps en UTC con formato ISO-8601.
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
